import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { MyConst } from "../data/MyConst";
import logoPdf from "assets/logo_pdf.png";

function buildMenuTree(items = []) {
  const mapped = items.map((item) => ({
    id: Number(item.id_menu),
    parentId: item.id_parent ? Number(item.id_parent) : null,
    code: item.menu_code,
    label: item.menu_name,
    to: item.route || "#",
    icon: item.icon || "",
    sortOrder: Number(item.sort_order || item.sortOrder || 0),
    children: [],
  }));

  const byId = new Map(mapped.map((item) => [item.id, item]));
  const roots = [];

  mapped.forEach((item) => {
    if (item.parentId && byId.has(item.parentId)) {
      byId.get(item.parentId).children.push(item);
      return;
    }
    roots.push(item);
  });

  const sortItems = (list) =>
    list
      .map((item) => ({
        ...item,
        children: sortItems(item.children || []),
      }))
      .sort((a, b) => (a.sortOrder - b.sortOrder) || (a.id - b.id));

  return sortItems(roots);
}

function isAccountMenu(item) {
  const accountCodes = ["PROFIL_SAYA", "PENGATURAN_BANTUAN", "PANDUAN_SISTEM", "LAPORKAN_MASALAH", "UBAH_PASSWORD", "LOGOUT"];
  const accountRoutes = ["/profil-saya", "/panduan-penggunaan", "/laporkan-masalah", "/ubah-password", "/logout"];
  return (
    accountCodes.includes(item?.code) ||
    accountRoutes.includes(item?.to) ||
    (item?.children || []).some((child) => isAccountMenu(child))
  );
}

function isActive(navitem, pathname) {
  if (navitem.to && navitem.to !== "#" && pathname === navitem.to) {
    return true;
  }
  return (navitem.children || []).some((child) => isActive(child, pathname));
}

function collectOpenState(items, pathname, open = {}) {
  items.forEach((item) => {
    if (isActive(item, pathname) && item.children?.length) {
      open[item.id] = true;
    }
    if (item.children?.length) {
      collectOpenState(item.children, pathname, open);
    }
  });
  return open;
}

function SidebarItem({ navitem, pathname, openMap, onToggle }) {
  const isOpen = Boolean(openMap?.[navitem.id]);

  if (navitem.children?.length) {
    return (
      <li className={`nav-item ${isOpen ? "menu-open" : ""}`}>
        <a
          href="/"
          onClick={(event) => {
            event.preventDefault();
            onToggle(navitem.id);
          }}
          className="nav-link"
        >
          <i className={`nav-icon fas ${navitem.icon || "fa-folder"}`} />
          <p>
            {navitem.label}
            <i className="right fas fa-angle-left" />
          </p>
        </a>
        <ul className="nav nav-treeview" style={{ display: isOpen ? "block" : "none" }}>
          {navitem.children.map((child) => (
            <SidebarItem key={child.code} navitem={child} pathname={pathname} openMap={openMap} onToggle={onToggle} />
          ))}
        </ul>
      </li>
    );
  }

  return (
    <li className="nav-item">
      <Link className={`nav-link ${pathname === navitem.to ? "active" : ""}`} to={navitem.to}>
        <i className={`nav-icon fas ${navitem.icon || "fa-circle"}`} />
        <p>{navitem.label}</p>
      </Link>
    </li>
  );
}

function profilePhotoUrl(filename, location = "profile") {
  if (!filename) {
    return `${process.env.PUBLIC_URL || ""}/assets/dist/img/avatar.png`;
  }
  return `${String(MyConst.URLASET || "").replace(/\/$/, "")}/${location || "profile"}/${filename}`;
}

function assetUrl(location, filename) {
  if (!filename) return "";
  return `${String(MyConst.URLASET || "").replace(/\/$/, "")}/${location}/${filename}`;
}

function formatKoniName(value) {
  const name = String(value || "").trim();
  if (!name) return "";
  return /^(koni|komite olahraga nasional indonesia)/i.test(name) ? name : `KONI ${name}`;
}

function sidebarBrandData(user) {
  const groupName = String(user?.groupname || "").toUpperCase();
  const header = user?.pdf_header || {};
  const useKoniKabKota = !["SUPERADMIN", "ADMIN_KONI_PROVINSI"].includes(groupName);
  const koniKabKotaName = formatKoniName(
    header?.koni_kabkota_name ||
      header?.koni_wilayah ||
      user?.personal_koni_kabkota_name,
  );
  const koniKabKotaLogo = header?.koni_kabkota_logo || "";

  return {
    logoSrc: useKoniKabKota && koniKabKotaLogo ? assetUrl("organisasi-logo", koniKabKotaLogo) : logoPdf,
    title: MyConst.SYS_SHORT_NAME || "SIMAKOR",
    subtitle: useKoniKabKota ? koniKabKotaName : "",
  };
}

export default function Sidebar({ menus, username, aliasname, foto, fotoLocation = "profile", user }) {
  const navItems = useMemo(() => buildMenuTree(menus), [menus]);
  const mainNavItems = useMemo(() => navItems.filter((item) => !isAccountMenu(item)), [navItems]);
  const accountNavItems = useMemo(() => navItems.filter((item) => isAccountMenu(item)), [navItems]);
  const brand = useMemo(() => sidebarBrandData(user), [user]);
  const location = useLocation();
  const pathname = location?.pathname || "/";
  const [openMap, setOpenMap] = useState({});

  useEffect(() => {
    setOpenMap(collectOpenState(navItems, pathname));
  }, [navItems, pathname]);

  const toggleItem = (id) => {
    setOpenMap((prev) => ({ ...prev, [id]: !prev?.[id] }));
  };

  return (
    <aside className="main-sidebar sidebar-dark-primary elevation-4">
      <a href="/" onClick={(event) => event.preventDefault()} className="brand-link kms-sidebar-brand">
        <img
          src={brand.logoSrc}
          alt="Logo"
          className="kms-brand-logo"
          onError={(event) => {
            event.currentTarget.onerror = null;
            event.currentTarget.src = logoPdf;
          }}
        />
        <span className="kms-brand-text">
          <span className="kms-brand-title">{brand.title}</span>
          {brand.subtitle ? <span className="kms-brand-subtitle">{brand.subtitle}</span> : null}
        </span>
      </a>

      <div className="sidebar">
        <div className="user-panel kms-sidebar-user-panel mt-3 pb-3 mb-3 d-flex align-items-center">
          <div
            className="image flex-shrink-0"
            style={{
              width: 38,
              minWidth: 38,
              marginLeft: "0.3rem",
              paddingLeft: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              src={profilePhotoUrl(foto, fotoLocation)}
              className="img-circle elevation-2"
              alt="User"
              style={{ width: 34, height: 34, objectFit: "cover", objectPosition: "center" }}
            />
          </div>
          <div className="info" style={{ flex: "1 1 auto", minWidth: 0 }}>
            <span
              className="d-block text-white"
              style={{ whiteSpace: "normal", overflowWrap: "anywhere", lineHeight: 1.25 }}
            >
              {aliasname || username}
            </span>
          </div>
        </div>

        <nav className="mt-2">
          <ul className="nav nav-pills nav-sidebar flex-column" role="menu">
            <li className="nav-header">MENU UTAMA</li>
            {mainNavItems.map((navitem) => (
              <SidebarItem key={navitem.code} navitem={navitem} pathname={pathname} openMap={openMap} onToggle={toggleItem} />
            ))}
            {accountNavItems.length > 0 ? <li className="nav-header mt-3">AKUN SAYA</li> : null}
            {accountNavItems.map((navitem) => (
              <SidebarItem key={navitem.code} navitem={navitem} pathname={pathname} openMap={openMap} onToggle={toggleItem} />
            ))}
          </ul>
        </nav>
      </div>
    </aside>
  );
}
